import dayjs, { Dayjs } from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

// Enable timezone-aware parsing/formatting globally. dayjs is a singleton, so
// extending here applies to every `dayjs` import across the app.
dayjs.extend(utc);
dayjs.extend(timezone);

/**
 * Default timezone for the dashboard.
 *
 * All backend time-series data points are timestamped at the building's local
 * time (Asia/Bangkok, UTC+7). Month/day bucketing of those points MUST be
 * pinned to this zone, otherwise a viewer in another timezone (or a headless
 * browser running in UTC) buckets each point into the wrong period — e.g. a
 * `2026-05-01 00:00:00+07:00` point becomes `2026-04-30 17:00 UTC` and is
 * mislabelled as April, dropping the latest month from monthly charts.
 *
 * See: _docs/frontend-bug-monthly-chart-timezone.md
 */
export const DEFAULT_APP_TIMEZONE = "Asia/Bangkok";

let appTimezone = DEFAULT_APP_TIMEZONE;

/**
 * Override the timezone used for all bucketing. Call once at app startup (see
 * `main.tsx`) to wire a deployment-specific value (e.g. `VITE_APP_TIMEZONE`)
 * so the same frontend can serve buildings in other regions. Empty/invalid
 * values are ignored and the current value is kept.
 */
export function setAppTimezone(tz?: string | null): void {
  if (typeof tz === "string" && tz.trim()) {
    appTimezone = tz.trim();
  }
}

/** The timezone currently used for all date bucketing. */
export function getAppTimezone(): string {
  return appTimezone;
}

/**
 * Convert a Unix timestamp (in seconds) to a Dayjs instance pinned to the app
 * timezone, independent of the host/browser timezone.
 */
export function unixToAppTz(unixSeconds: number): Dayjs {
  return dayjs.unix(unixSeconds).tz(getAppTimezone());
}

/**
 * Format a Unix timestamp (in seconds) in the app timezone.
 *
 * Use this instead of `dayjs.unix(x).format(...)` whenever the output buckets
 * a data point into a calendar period (month, day, hour), so the result does
 * not shift with the viewer's browser timezone.
 */
export function formatUnixInAppTz(unixSeconds: number, template: string): string {
  return unixToAppTz(unixSeconds).format(template);
}
